import type { DocumentNode, GraphQLFormattedError } from 'graphql';

import type {
  DeleteCurrentPushSubscriptionMutation,
  DeleteCurrentPushSubscriptionMutationVariables,
  PushNotificationCapabilityQuery,
  PushNotificationPreferencesQuery,
  TestCurrentPushSubscriptionMutation,
  TestCurrentPushSubscriptionMutationVariables,
  UpdatePushNotificationPreferenceMutation,
  UpdatePushNotificationPreferenceMutationVariables,
  UpsertCurrentPushSubscriptionMutation,
  UpsertCurrentPushSubscriptionMutationVariables,
  WebPushNotificationKind,
} from '@shared/api/graphql/generated';

import { getApolloRuntimeClient } from '@/shared/api';

import {
  DELETE_CURRENT_PUSH_SUBSCRIPTION,
  PUSH_NOTIFICATION_CAPABILITY,
  PUSH_NOTIFICATION_PREFERENCES,
  TEST_CURRENT_PUSH_SUBSCRIPTION,
  UPDATE_PUSH_NOTIFICATION_PREFERENCE,
  UPSERT_CURRENT_PUSH_SUBSCRIPTION,
} from './push-notifications.graphql';

export interface PushNotificationCapability {
  available: boolean;
  enabled: boolean;
  publicKey: string | null;
}

export interface PushNotificationPreference {
  kind: WebPushNotificationKind;
  enabled: boolean;
  available: boolean | null;
}

export type PushNotificationApiErrorKind = 'access-denied' | 'rate-limited' | 'unavailable';

export class PushNotificationApiError extends Error {
  constructor(readonly kind: PushNotificationApiErrorKind) {
    super(kind);
    this.name = 'PushNotificationApiError';
  }
}

const PUSH_OPERATION_DEADLINE_MS = 8_000;

const getPushOperationContext = (signal: AbortSignal) => ({
  // Ошибки мапятся локально: input содержит endpoint/ключи подписки.
  suppressGlobalErrorHandling: true,
  fetchOptions: { signal },
});

const withPushOperationDeadline = async <T>(
  operation: (signal: AbortSignal) => Promise<T>,
  externalSignal?: AbortSignal,
): Promise<T> => {
  const abortController = new AbortController();
  const abortFromExternalSignal = (): void => abortController.abort();
  let deadlineHandle: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    deadlineHandle = setTimeout(() => {
      abortController.abort();
      reject(new PushNotificationApiError('unavailable'));
    }, PUSH_OPERATION_DEADLINE_MS);
  });

  try {
    if (externalSignal?.aborted) {
      abortController.abort();
    } else {
      externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
    }

    return await Promise.race([operation(abortController.signal), deadline]);
  } finally {
    externalSignal?.removeEventListener('abort', abortFromExternalSignal);
    if (deadlineHandle !== undefined) {
      clearTimeout(deadlineHandle);
    }
  }
};

const getApiErrorKind = (errors: ReadonlyArray<GraphQLFormattedError> | undefined): PushNotificationApiErrorKind => {
  const rateLimited = errors?.some((error) => {
    const category = error.extensions?.category;
    const code = error.extensions?.code;
    const status = error.extensions?.status;

    const isLighthouseRateLimit = error.message.startsWith('Rate limit for Mutation.testWebPushSubscription exceeded.');

    return category === 'throttle' || code === 'RATE_LIMITED' || status === 429 || isLighthouseRateLimit;
  });

  if (rateLimited) {
    return 'rate-limited';
  }

  const accessDenied = errors?.some((error) => {
    const category = error.extensions?.category;
    const code = error.extensions?.code;
    const status = error.extensions?.status;
    const exception = error.extensions?.exception;

    return (
      category === 'authorization' ||
      code === 'FORBIDDEN' ||
      status === 403 ||
      (typeof exception === 'string' && exception.endsWith('AuthorizationException'))
    );
  });

  return accessDenied ? 'access-denied' : 'unavailable';
};

export const getPushNotificationCapability = async (
  externalSignal?: AbortSignal,
): Promise<PushNotificationCapability> => {
  try {
    const result = await withPushOperationDeadline(
      (signal) =>
        getApolloRuntimeClient().query<Partial<PushNotificationCapabilityQuery> | null | undefined>({
          query: PUSH_NOTIFICATION_CAPABILITY,
          // Императивное чтение должно получать актуальную capability, а не
          // возможное предыдущее значение из Apollo cache.
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
          context: getPushOperationContext(signal),
        }),
      externalSignal,
    );

    if (result.errors?.length || !result.data?.webPushContext?.capability) {
      return { available: false, enabled: false, publicKey: null };
    }

    return {
      available: true,
      enabled: result.data.webPushContext.capability.enabled,
      publicKey: result.data.webPushContext.capability.public_key ?? null,
    };
  } catch {
    return { available: false, enabled: false, publicKey: null };
  }
};

export const getPushNotificationPreferences = async (
  externalSignal?: AbortSignal,
): Promise<PushNotificationPreference[]> => {
  try {
    const result = await withPushOperationDeadline(
      (signal) =>
        getApolloRuntimeClient().query<Partial<PushNotificationPreferencesQuery> | null | undefined>({
          query: PUSH_NOTIFICATION_PREFERENCES,
          // Preferences загружаются императивно и должны отражать текущие
          // permissions и сохранённые настройки на момент открытия страницы.
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
          context: getPushOperationContext(signal),
        }),
      externalSignal,
    );
    const preferences = result.data?.webPushContext?.preferences;

    if (result.errors?.length || !preferences) {
      throw new PushNotificationApiError(getApiErrorKind(result.errors));
    }

    return preferences.map(({ kind, enabled, available }) => ({ kind, enabled, available: available ?? null }));
  } catch (error) {
    if (error instanceof PushNotificationApiError) {
      throw error;
    }

    throw new PushNotificationApiError('unavailable');
  }
};

const assertMutationSucceeded = (errors: ReadonlyArray<GraphQLFormattedError> | undefined, hasData: boolean): void => {
  if (errors?.length || !hasData) {
    throw new PushNotificationApiError(getApiErrorKind(errors));
  }
};

const runPushMutationWithResult = async <TData, TVariables extends Record<string, unknown>, TResult>(
  mutation: DocumentNode,
  variables: TVariables,
  pickData: (data: TData | null | undefined) => TResult | null | undefined,
  externalSignal?: AbortSignal,
): Promise<TResult> => {
  try {
    const result = await withPushOperationDeadline(
      (signal) =>
        getApolloRuntimeClient().mutate<TData, TVariables>({
          mutation,
          variables,
          errorPolicy: 'all',
          context: getPushOperationContext(signal),
        }),
      externalSignal,
    );

    const pickedData = pickData(result.data);
    assertMutationSucceeded(result.errors, Boolean(pickedData));

    return pickedData as TResult;
  } catch (error) {
    if (error instanceof PushNotificationApiError) {
      throw error;
    }

    throw new PushNotificationApiError('unavailable');
  }
};

const runPushMutation = async <TData, TVariables extends Record<string, unknown>>(
  mutation: DocumentNode,
  variables: TVariables,
  pickData: (data: TData | null | undefined) => unknown,
  externalSignal?: AbortSignal,
): Promise<void> => {
  await runPushMutationWithResult(mutation, variables, pickData, externalSignal);
};

export const upsertPushNotificationSubscription = (
  input: {
    endpoint: string;
    p256dh: string;
    auth: string;
  },
  externalSignal?: AbortSignal,
): Promise<void> =>
  runPushMutation<UpsertCurrentPushSubscriptionMutation, UpsertCurrentPushSubscriptionMutationVariables>(
    UPSERT_CURRENT_PUSH_SUBSCRIPTION,
    { input },
    (data) => data?.upsertWebPushSubscription,
    externalSignal,
  );

export const deletePushNotificationSubscription = (endpoint: string, externalSignal?: AbortSignal): Promise<void> =>
  runPushMutation<DeleteCurrentPushSubscriptionMutation, DeleteCurrentPushSubscriptionMutationVariables>(
    DELETE_CURRENT_PUSH_SUBSCRIPTION,
    { input: { endpoint } },
    (data) => data?.deleteWebPushSubscription,
    externalSignal,
  );

export const testPushNotificationSubscription = (endpoint: string, externalSignal?: AbortSignal): Promise<void> =>
  runPushMutation<TestCurrentPushSubscriptionMutation, TestCurrentPushSubscriptionMutationVariables>(
    TEST_CURRENT_PUSH_SUBSCRIPTION,
    { input: { endpoint } },
    (data) => data?.testWebPushSubscription,
    externalSignal,
  );

export const updatePushNotificationPreference = async (
  kind: WebPushNotificationKind,
  enabled: boolean,
  externalSignal?: AbortSignal,
): Promise<PushNotificationPreference[]> => {
  const user = await runPushMutationWithResult<
    UpdatePushNotificationPreferenceMutation,
    UpdatePushNotificationPreferenceMutationVariables,
    UpdatePushNotificationPreferenceMutation['updateWebPushNotificationPreference']
  >(
    UPDATE_PUSH_NOTIFICATION_PREFERENCE,
    { input: { kind, enabled } },
    (data) => data?.updateWebPushNotificationPreference,
    externalSignal,
  );

  return user.web_push_notification_preferences.map((preference) => ({
    kind: preference.kind,
    enabled: preference.enabled,
    available: preference.available ?? null,
  }));
};
